import { PropsWithChildren, useState } from "react"
import { useTranslation } from "react-i18next"
import { UserListItem } from "@repo/core/models"
import {
  type Column,
  ColumnFiltersState,
  createColumnHelper,
  getCoreRowModel,
  getFilteredRowModel,
  getPaginationRowModel,
  getSortedRowModel,
  SortingState,
  useReactTable,
} from "@tanstack/react-table"
import { format } from "date-fns"
import { Crown, InfoIcon, KeyRoundIcon, PencilIcon, XIcon } from "lucide-react"

import Input from "@/components/core/Input"
import { ToggleSortButton } from "@/components/DashboardTable"
import Icon from "@/components/Icon"
import { EmptyValue } from "@/components/table-helpers"
import { Badge, BadgeProps } from "@/components/ui/badge"
import { Button } from "@/components/ui/button"
import { ButtonWithOptions } from "@/components/ui/button-with-options"
import {
  DropdownMenuGroup,
  DropdownMenuItem,
} from "@/components/ui/dropdown-menu"
import { StatelessDataTable } from "@/components/ui/stateless-data-table"
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@/components/ui/tooltip"
import { formatFullName } from "@/helpers/names"

const columnHelper = createColumnHelper<UserListItem>()

type Props = {
  actions: React.ReactNode
  isLoading: boolean
  onEdit: (userId: string) => void
  onResendPassword: (userId: string) => void
  users: UserListItem[]
}
export function UsersTable({
  actions,
  isLoading,
  onEdit,
  onResendPassword,
  users,
}: Props) {
  const columns = useColumns({ onEdit, onResendPassword })

  const [sorting, setSorting] = useState<SortingState>([
    { id: "updated_at", desc: true },
  ])
  const [columnFilters, setColumnFilters] = useState<ColumnFiltersState>([])

  const table = useReactTable<UserListItem>({
    data: users,
    getRowId: (row) => row.email,
    columns,
    getCoreRowModel: getCoreRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    getSortedRowModel: getSortedRowModel(),
    onSortingChange: setSorting,
    onColumnFiltersChange: setColumnFilters,
    getFilteredRowModel: getFilteredRowModel(),
    state: {
      columnFilters,
      sorting,
    },
    initialState: {
      pagination: {
        pageSize: 50,
      },
    },
  })

  return (
    <div className="flex flex-col gap-4">
      <div className="flex items-center justify-between">
        <div>
          <SearchBox
            value={(table.getColumn("name")?.getFilterValue() as string) || ""}
            onChange={(event) =>
              table.getColumn("name")?.setFilterValue(event.target.value)
            }
            onReset={() => table.getColumn("name")?.setFilterValue("")}
          />
        </div>
        <div>{actions}</div>
      </div>
      <StatelessDataTable
        isLoading={isLoading}
        table={table}
        getCellClassName={getCellClassName}
      />
    </div>
  )
}

/** Highlight specific statuses */
function getCellClassName(row: UserListItem) {
  if (row.status === "NOT_FOUND") {
    return "bg-warning-100"
  }

  return ""
}

function useColumns({
  onEdit,
  onResendPassword,
}: Pick<Props, "onEdit" | "onResendPassword">) {
  const { t } = useTranslation()
  const columns = [
    columnHelper.accessor((row) => row.last_name + " " + row.first_name, {
      id: "name",
      header: ({ column }) => {
        return (
          <SortableColumnHeader column={column}>
            {t("users.list.name")}
          </SortableColumnHeader>
        )
      },
      cell: (cols) => {
        const user = cols.cell.row.original

        return (
          <div className="flex items-center gap-2">
            {formatFullName(user.first_name || null, user.last_name || null)}
            {user.groups.includes("Manager") && (
              <Crown className="size-4 text-neutral-500" />
            )}
          </div>
        )
      },
    }),
    columnHelper.accessor("groups", {
      header: ({ column }) => {
        return (
          <SortableColumnHeader column={column}>
            {t("users.list.role")}
          </SortableColumnHeader>
        )
      },
      cell({ getValue }) {
        return <UserGroups groups={getValue()} />
      },
    }),
    columnHelper.accessor("email", {
      header: ({ column }) => {
        return (
          <SortableColumnHeader column={column}>
            {t("users.list.email")}
          </SortableColumnHeader>
        )
      },
    }),
    columnHelper.accessor("queues", {
      header: t("users.list.queues"),
      cell({ getValue }) {
        const queues = getValue()
        if (queues.length === 0) {
          return <EmptyValue />
        }

        return (
          <div className="flex flex-col gap-2 text-xs">
            {queues.map((queue) => (
              <div key={queue}>{queue}</div>
            ))}
          </div>
        )
      },
    }),
    columnHelper.accessor("updated_at", {
      header: ({ column }) => {
        return (
          <SortableColumnHeader column={column}>
            {t("users.list.updatedAt")}
          </SortableColumnHeader>
        )
      },
      cell({ getValue }) {
        const date = getValue()

        return date ? <>{format(date, "yyyy/MM/dd HH:mm")}</> : <EmptyValue />
      },
    }),
    columnHelper.accessor("status", {
      header: ({ column }) => {
        return (
          <SortableColumnHeader column={column}>
            {t("users.list.status")}
          </SortableColumnHeader>
        )
      },
      cell({ getValue }) {
        return <UserStatus status={getValue()} />
      },
    }),
    columnHelper.accessor("created_at", {
      header: ({ column }) => {
        return (
          <SortableColumnHeader column={column}>
            {t("users.list.createdAt")}
          </SortableColumnHeader>
        )
      },
      cell({ getValue }) {
        const date = getValue()

        return date ? <>{format(date, "yyyy/MM/dd HH:mm")}</> : <EmptyValue />
      },
    }),
    columnHelper.accessor("connect_user_id", {
      header: () => {
        return null
      },
      cell({ row }) {
        const userId = row.original.cognito_user_id
        if (!userId) return null
        const status = row.original.status

        const canResendPassword = status === "FORCE_CHANGE_PASSWORD"

        return (
          <ButtonWithOptions
            mainActionButton={
              <Button
                onClick={() => onEdit(userId)}
                variant="outline"
                className="rounded-none rounded-l"
              >
                <PencilIcon className="size-4" />
                {t("global.button.edit")}
              </Button>
            }
            dropdownMenuContent={
              <>
                <DropdownMenuGroup>
                  <DropdownMenuItem
                    onClick={
                      canResendPassword
                        ? () => onResendPassword(userId)
                        : undefined
                    }
                    className="font-button-medium text-sm"
                    disabled={!canResendPassword}
                  >
                    <div className="flex items-center gap-2">
                      <KeyRoundIcon className="size-6" />
                      {t("users.resendPassword.action")}
                    </div>
                  </DropdownMenuItem>
                </DropdownMenuGroup>
              </>
            }
            variant="outline"
            isDisabled={!canResendPassword}
          />
        )
      },
    }),
  ]

  return columns
}

export function UserGroups({ groups }: { groups: string[] }) {
  const { t } = useTranslation()

  if (groups.length === 0) {
    const tooltip = t("users.list.missingRole.tooltip")

    return (
      <Tooltip>
        <Badge variant="warning" className="gap-1 text-xs">
          <span>{t("users.list.missingRole.label")}</span>
          <TooltipTrigger asChild>
            <button
              type="button"
              tabIndex={-1}
              className="inline-flex cursor-help rounded-full"
              aria-label={tooltip}
            >
              <InfoIcon className="size-3.5" />
            </button>
          </TooltipTrigger>
        </Badge>
        <TooltipContent className="max-w-96 whitespace-normal">
          {tooltip}
        </TooltipContent>
      </Tooltip>
    )
  }

  return <>{groups.map((group) => t("users.groups." + group)).join(", ")}</>
}

function SortableColumnHeader({
  children,
  column,
}: PropsWithChildren<{
  column: Column<UserListItem>
}>) {
  return (
    <BasicSortableColumnHeader
      direction={column.getIsSorted() || undefined}
      onToggle={() => column.toggleSorting(column.getIsSorted() !== "desc")}
    >
      {children}
    </BasicSortableColumnHeader>
  )
}

function BasicSortableColumnHeader({
  children,
  direction,
  onToggle,
}: PropsWithChildren<{
  direction?: "asc" | "desc"
  onToggle: () => void
}>) {
  return (
    <div className="flex items-center justify-start">
      {children}
      <ToggleSortButton direction={direction || undefined} onClick={onToggle} />
    </div>
  )
}

function SearchBox({
  onChange,
  onReset,
  placeholder,
  value,
}: {
  onChange: (event: React.ChangeEvent<HTMLInputElement>) => void
  onReset: () => void
  placeholder?: string
  value: string
}) {
  return (
    <div className="relative">
      <Input
        name="search"
        value={value}
        onChange={onChange}
        target={<Icon name="search" className="text-muted-foreground" />}
        inputClassName="h-10 min-w-[300px]"
        placeholder={placeholder || "dashboard.selectOptions.searchPlaceholder"}
      />
      <Button
        onClick={onReset}
        variant="ghost"
        size="icon"
        className="absolute top-2 right-2 size-6 max-w-[300px]"
      >
        <XIcon className="size-4" />
      </Button>
    </div>
  )
}

function UserStatus({ status }: { status: string }) {
  const { t } = useTranslation()

  return (
    <UserStatusBadge status={status}>
      {t("users.statuses." + status)}
    </UserStatusBadge>
  )
}

function UserStatusBadge({
  children,
  status,
}: PropsWithChildren<{ status: string }>) {
  const variants: {
    [key: string]: BadgeProps["variant"]
  } = {
    NOT_FOUND: "destructive",
    DELETED: "destructive",
    CONFIRMED: "success",
    FORCE_CHANGE_PASSWORD: "warning",
  }

  return (
    <Badge className="text-xs" variant={variants[status] || "default"}>
      {children}
    </Badge>
  )
}
